import React, { useState } from 'react';

import Button from 'CommonComponents/Button';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import TextButton from 'CommonComponents/TextButton';

import { CopyToClipboardProvider } from 'Context/CopyToClipboardContext';

import { useCopyToClipboard } from 'Hooks/useCopyToClipboard';

import CopyIcon from 'Assets/icons/copy.svg';
import EyeIcon from 'Assets/icons/eye.svg';

export default function MfaAddDevice(props) {
    const [deviceAdded, setDeviceAdded] = useState(false);
    const [requestFailed, setRequestFailed] = useState(false);
    const [request, setRequest] = useState(false);
    const [code, setCode] = useState('');
    const [addDeviceError, setAddDeviceError] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const [closing, setClosing] = useState(false);

    const errorMessage = $t(`The code you entered was incorrect. Please check your device’s time and date are correct and try again.`);

    const recoveryCodesMessage = $t(
        `The recovery codes below can be used to access your account if you lose your two-factor device or delete your Authenticator app.`,
    );

    const addDevice = () => {
        setRequest(true);
        setAddDeviceError(false);

        axios
            .post(props.confirmUrl, { code })
            .then(() => {
                setDeviceAdded(true);

                axios.post(props.sendDeviceAddedUrl, {}).catch(console.log);

                axios
                    .get(props.recoveryCodesUrl)
                    .then((response) => setRecoveryCodes(response.data))
                    .catch(() => setRequestFailed(true))
                    .finally(() => setRequest(false));
            })
            .catch(() => {
                setAddDeviceError(true);
                setRequest(false);
            });
    };

    const internalCloseHandler = () => {
        if (deviceAdded && !requestFailed && !closing) {
            setClosing(true);
        } else {
            setDeviceAdded(false);
            setRequestFailed(false);
            setClosing(false);
            setCode('');
            props.closeHandler();
        }
    };

    const RecoveryCodesModal = ({ recoveryCodes, recoveryCodesMessage, setClosing, request }) => {
        useCopyToClipboard('recovery-codes-button', 'recovery-codes');

        return (
            <>
                <div className="tw-grid tw-grid-cols-1">
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <div className="alert alert-success tw-mt-2" role="alert">
                            {$t('Your two-factor device was added.')}
                        </div>
                        <p className="modal__text modal__text-color">{recoveryCodesMessage}</p>
                        <p className="modal__text modal__text-color">{$t('The recovery codes will only be shown once and then will not be available.')}</p>
                        <p className="modal__text modal__text-color">
                            <b>{$t('Please save them somewhere safe and do not share them.')}</b>
                        </p>

                        <div className="tw-flex tw-items-start tw-w-3/5 tw-mx-auto tw-gap-4">
                            <ul className="modal__text modal__text-color  tw-border tw-border-gray-300 tw-p-2 tw-rounded  tw-flex-1">
                                {recoveryCodes.map((rc, index) => (
                                    <li key={index}>{rc}</li>
                                ))}
                            </ul>
                            <button id="recovery-codes-button" type="button" className="primary">
                                <CopyIcon className="tw-w-6 tw-h-6" />
                            </button>
                        </div>
                    </div>
                    <textarea id="recovery-codes" className="tw-opacity-0 tw-absolute" value={recoveryCodes.join('\n')} readOnly />
                </div>
                <div className="modal__footer">
                    <Button variant="secondary" onClick={() => setClosing(true)} disabled={request}>
                        {$t('Close')}
                    </Button>
                </div>
            </>
        );
    };

    return (
        <div id={props.modalId} ref={props.modalRef} tabIndex="-1" aria-hidden="true" className="hidden modal__box">
            <div className="modal__container">
                <div className="modal__inner-container">
                    <ModalHeader title={$t('Authenticator App')} close={internalCloseHandler} />
                    <div className="tw-rounded-b">
                        {props.showModal && (
                            <div className="modal__form-container">
                                {!deviceAdded && !requestFailed && (
                                    <div className="tw-grid tw-grid-cols-1">
                                        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-w-full tw-space-y-1.5">
                                            <div dangerouslySetInnerHTML={{ __html: props.qrCode }} />
                                            <a href={props.codeUrl} target="_blank" rel="noreferrer">
                                                <TextButton variant="primary">
                                                    <EyeIcon className="tw-w-3 tw-h-3" /> View code
                                                </TextButton>
                                            </a>
                                        </div>
                                        <div className="tw-flex tw-flex-col tw-space-y-1.5 tw-mb-2">
                                            <label className="text-input__label">{$t('TOTP code')}:</label>
                                            <input className="tw-border-0 text-input" type="text" value={code} onChange={(e) => setCode(e.target.value)} />
                                            {addDeviceError && <div className="tw-text-red-500 tw-text-sm">{errorMessage}</div>}
                                        </div>
                                        <ModalFooter
                                            cancelButtonText={$t('Cancel')}
                                            primaryButtonText={$t('Save')}
                                            onCancel={props.closeHandler}
                                            handleSubmit={addDevice}
                                            isSubmitting={request || !code}
                                        />
                                    </div>
                                )}

                                {deviceAdded && !requestFailed && !closing && (
                                    <CopyToClipboardProvider>
                                        <RecoveryCodesModal
                                            recoveryCodes={recoveryCodes}
                                            recoveryCodesMessage={recoveryCodesMessage}
                                            setClosing={() => setClosing(true)}
                                            request={request}
                                        />
                                    </CopyToClipboardProvider>
                                )}
                                {closing && (
                                    <>
                                        <div className="tw-grid tw-grid-cols-1">
                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                <p className="modal__text modal__text-color">
                                                    {$t('Have you saved your recovery codes? They will not be available after closing this window.')}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="modal__footer">
                                            <Button variant="secondary" onClick={() => setClosing(false)} disabled={request}>
                                                {$t('Cancel')}
                                            </Button>
                                            <Button variant="danger" onClick={internalCloseHandler} disabled={request}>
                                                {$t('Close')}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
